export const webappTarget = process.env.REACT_APP_WEBAPP_TARGET || 'portal';

const normalizePathPrefix = (value, fallback) => {
  const raw = (value || fallback || '').trim();
  if (!raw) {
    return '';
  }
  const withLeadingSlash = raw.startsWith('/') ? raw : `/${raw}`;
  return withLeadingSlash.replace(/\/+$/, '') || fallback;
};

export const apiVersionPrefix = normalizePathPrefix(
  process.env.REACT_APP_API_VERSION_PREFIX,
  '/v1',
);
const normalizeApiBase = value => String(value || '').replace(/\/+$/, '');
const retiredLocalProxyPorts = new Set(['8082', '8083']);
const browserHostname = () => {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return '';
  }
  return window.location.hostname;
};

function browserReachableLocalURL(value = '') {
  const normalized = normalizeApiBase(value);
  const hostname = browserHostname();
  if (!normalized || !hostname) {
    return normalized;
  }
  try {
    const parsed = new URL(normalized);
    if (!['localhost', '127.0.0.1', '0.0.0.0'].includes(parsed.hostname)) {
      return normalized;
    }
    if (retiredLocalProxyPorts.has(parsed.port)) {
      return '';
    }
    parsed.hostname = hostname;
    return parsed.toString().replace(/\/+$/, '');
  } catch (error) {
    return normalized;
  }
}

const compatApiBase = process.env.REACT_APP_API_BASE;
const localApiBase = port => `http://${browserHostname() || 'localhost'}:${port}/api${apiVersionPrefix}`;
const firstBrowserReachableApiBase = candidates => candidates.map(browserReachableLocalURL).find(Boolean) || '';
const resolvedApiBase = (candidates, fallback) => normalizeApiBase(firstBrowserReachableApiBase(candidates) || fallback);
export const identityApiBase = resolvedApiBase([process.env.REACT_APP_IDENTITY_API_BASE, process.env.REACT_APP_AUTH_API_BASE, compatApiBase], localApiBase(8091));
export const userApiBase = resolvedApiBase([process.env.REACT_APP_USER_API_BASE, compatApiBase], localApiBase(8084));
export const adminApiBase = resolvedApiBase([process.env.REACT_APP_ADMIN_API_BASE, compatApiBase], identityApiBase);
export const contentApiBase = resolvedApiBase([process.env.REACT_APP_CONTENT_API_BASE, compatApiBase], localApiBase(8092));
export const examApiBase = resolvedApiBase([process.env.REACT_APP_EXAM_API_BASE, compatApiBase], localApiBase(8093));
export const mediaApiBase = resolvedApiBase([process.env.REACT_APP_MEDIA_API_BASE, compatApiBase], localApiBase(8094));
export const analyticsApiBase = resolvedApiBase([process.env.REACT_APP_ANALYTICS_API_BASE, compatApiBase], localApiBase(8095));
export const adminReviewApiBase = resolvedApiBase([process.env.REACT_APP_ADMIN_REVIEW_API_BASE, compatApiBase], localApiBase(8096));
export const operationsApiBase = resolvedApiBase([process.env.REACT_APP_OPERATIONS_API_BASE, compatApiBase], localApiBase(8097));
export const projectionApiBase = resolvedApiBase([process.env.REACT_APP_PROJECTION_API_BASE, compatApiBase], localApiBase(8098));
export const billingApiBase = resolvedApiBase([process.env.REACT_APP_BILLING_API_BASE, compatApiBase], localApiBase(8099));
export const studentApiBase = userApiBase;
export const apiBase = webappTarget === 'admin'
  ? adminApiBase
  : webappTarget === 'subscription' ? billingApiBase : userApiBase;

const routeStartsWith = (path, prefixes) => prefixes.some(prefix => path === prefix || path.startsWith(`${prefix}/`));
const apiRoutePrefix = `/api${apiVersionPrefix}`;

function routePathWithoutApiPrefix(path) {
  if (path === apiRoutePrefix) {
    return '/';
  }
  if (path.startsWith(`${apiRoutePrefix}/`)) {
    return path.slice(apiRoutePrefix.length) || '/';
  }
  return path;
}

export function apiBaseForPath(path = '') {
  const normalizedPath = String(path || '').startsWith('/') ? String(path || '') : `/${path || ''}`;
  const routePath = routePathWithoutApiPrefix(normalizedPath.split(/[?#]/, 1)[0]);
  if (
    routeStartsWith(routePath, ['/auth', '/presence']) ||
    routePath === '/register-student' ||
    (routePath.startsWith('/parent/children/') && (
      routePath.endsWith('/account') ||
      routePath.endsWith('/archive') ||
      routePath.endsWith('/unarchive')
    )) ||
    routeStartsWith(routePath, [
      '/admin/users-dashboard',
      '/internal/identity',
      '/organization',
      '/possible-roles',
      '/privileges',
      '/role-privileges',
      '/roles',
      '/users',
    ])
  ) {
    return identityApiBase;
  }
  if (routeStartsWith(routePath, ['/parent', '/student'])) {
    return userApiBase;
  }
  if (routeStartsWith(routePath, ['/admin/students', '/admin/mock-papers'])) {
    return userApiBase;
  }
  if (routePath === '/admin/system/projections/health') {
    return projectionApiBase;
  }
  if (routeStartsWith(routePath, ['/billing', '/internal/billing'])) {
    return billingApiBase;
  }
  if (routeStartsWith(routePath, ['/admin/system'])) {
    return operationsApiBase;
  }
  if (routeStartsWith(routePath, ['/admin/agent'])) {
    return adminReviewApiBase;
  }
  if (routeStartsWith(routePath, ['/admin/dashboard', '/admin/metrics-dashboard', '/admin/learner-analytics', '/internal/analytics'])) {
    return analyticsApiBase;
  }
  if (routeStartsWith(routePath, ['/images', '/videos', '/internal/media'])) {
    return mediaApiBase;
  }
  if (
    routeStartsWith(routePath, [
      '/admin/exam-workspace',
      '/admin/question-review-print',
      '/exam-exercises',
      '/exam-question-answers',
      '/exam-questions',
      '/exams',
      '/mock-exams',
      '/predicted-papers',
      '/questions',
    ])
  ) {
    return examApiBase;
  }
  if (
    routeStartsWith(routePath, [
      '/admin/content-workspace',
      '/answers',
      '/curriculum',
      '/curriculum-topics',
      '/exercises',
      '/internal/content',
      '/modules',
      '/topics',
    ])
  ) {
    return contentApiBase;
  }
  return apiBase;
}
export const agentApiVersionPrefix = normalizePathPrefix(
  process.env.REACT_APP_AGENT_API_VERSION_PREFIX,
  '/v1',
);
export const agentApiBaseUrl = (
  process.env.REACT_APP_AGENT_API_BASE_URL || 'https://agent-api.mentoringmaths.com'
).replace(/\/+$/, '');
export const agentApiHealthUrl = `${agentApiBaseUrl}${agentApiVersionPrefix}/health`;
export const agentApiDocsUrl = `${agentApiBaseUrl}${agentApiVersionPrefix}/docs`;
export const agentApiOpenApiUrl = `${agentApiBaseUrl}${agentApiVersionPrefix}/openapi.yaml`;
export const publicBaseUrl = process.env.REACT_APP_PUBLIC_BASE_URL || '';
export const adminBasePath = process.env.REACT_APP_ADMIN_BASE_PATH ?? (webappTarget === 'admin' ? '' : '/admin');
export const studentBasePath = process.env.REACT_APP_STUDENT_BASE_PATH || '';
export const parentBasePath = process.env.REACT_APP_PARENT_BASE_PATH || '/parent';
export const subscriptionBasePath = resolveSubscriptionBasePath();
export const publicHomeHref = resolvePublicHomeHref();
export const publicHomeStartLearningHref = resolveConfiguredHref(
  process.env.REACT_APP_PUBLIC_HOME_START_LEARNING_URL,
  localSubscriptionHref() || subscriptionBasePath,
);
export const publicHomeSubscribeHref = resolveConfiguredHref(
  process.env.REACT_APP_PUBLIC_HOME_SUBSCRIBE_URL,
  publicHomeStartLearningHref,
);
export const releaseVersion = process.env.REACT_APP_RELEASE_VERSION || 'dev';
export const authTokenStorageKey = 'math-platform-auth-token';
export const authRefreshTokenStorageKey = 'math-platform-auth-refresh-token';
export const authSessionStorageKey = 'math-platform-auth-session';

function resolveSubscriptionBasePath() {
  const configuredBasePath = String(process.env.REACT_APP_SUBSCRIPTION_BASE_PATH || '').trim();
  if (configuredBasePath) {
    return browserReachableLocalURL(configuredBasePath);
  }
  if (typeof window !== 'undefined') {
    const hostname = String(window.location?.hostname || '').trim().toLowerCase();
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3003';
    }
  }
  return '/subscription';
}

function resolveConfiguredHref(value, fallback) {
  const configuredHref = String(value || '').trim();
  return configuredHref || fallback;
}

function resolvePublicHomeHref() {
  const configuredHref = String(process.env.REACT_APP_PUBLIC_HOME_URL || '').trim();
  const localHref = localPublicHomeHref();
  if (configuredHref && !(configuredHref === '/' && localHref)) {
    return configuredHref;
  }
  return localHref || '/';
}

function localPublicHomeHref() {
  if (typeof window === 'undefined' || webappTarget === 'portal') {
    return '';
  }
  const hostname = String(window.location?.hostname || '').trim().toLowerCase();
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `http://${hostname}:3000`;
  }
  return '';
}

function localSubscriptionHref() {
  if (typeof window === 'undefined') {
    return '';
  }
  const hostname = String(window.location?.hostname || '').trim().toLowerCase();
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `http://${hostname}:3003`;
  }
  return '';
}
